/**
 * Column definitions for the "Decyzje klienta" review DataTable. Mirrors
 * apps/mockups/.../table-columns.mockup.tsx's decision-badge / reservation-chip
 * mapping and SILENT-aware row tint, adapted to the real UnitDecisionsListItem
 * contract shape, real i18n strings, and the shared date-format helper.
 */
import { Check, X, Clock, AlertTriangle } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { Badge } from '@ooh/ui/components/badge';
import { useColumnPreferences } from '@ooh/ui/components/data-table-column-preferences';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import type { UnitDecisionsListItem, UnitDecisionReservationState } from '@ooh/contracts';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import { getUnitDisplayState, type UnitDecisionDisplayState } from './filters';
import { formatOfferedPrice } from './format-helpers';
import { buildDecisionSharedColumns } from './decision-shared-fields';
import { buildOrderedVisibleColumns } from '@ooh/ui/lib/ordered-visible-columns';

export const UNIT_DECISIONS_COLUMN_STORAGE_PREFIX = 'ooh:offer-decisions';
export const UNIT_DECISIONS_PINNED_COLUMN_IDS = ['decision'];

export function useUnitDecisionsColumnPreferences(allColumnIds: string[]) {
  return useColumnPreferences({
    storageKeyPrefix: UNIT_DECISIONS_COLUMN_STORAGE_PREFIX,
    allColumnIds,
    pinnedIds: UNIT_DECISIONS_PINNED_COLUMN_IDS,
  });
}

export function useUnitDecisionTableColumns(
  t: TFunction<'offers'>,
  canViewSupplierDetails: boolean,
) {
  const columns = useMemo(
    () => buildUnitDecisionColumns({ t, canViewSupplierDetails }),
    [canViewSupplierDetails, t],
  );
  const allColumnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const preferences = useUnitDecisionsColumnPreferences(allColumnIds);
  const ordered = useMemo(
    () =>
      buildOrderedVisibleColumns(
        columns,
        preferences.columnOrder,
        preferences.visibleColumnIds,
        preferences.columnWidths,
      ),
    [columns, preferences.columnOrder, preferences.visibleColumnIds, preferences.columnWidths],
  );
  return { columns, preferences, ...ordered };
}

function formatAcceptedDates(periods: UnitDecisionsListItem['periods']): string {
  if (!periods || periods.length === 0) return '';
  return periods.map((p) => formatDisplayDateRange(p.from, p.to)).join(', ');
}

// ─── Decision badge config ─────────────────────────────────────

interface BadgeConfig {
  label: string;
  icon: typeof Check;
  variant: 'success' | 'destructive' | 'outline' | 'warning';
  tooltip?: string;
}

function buildBadgeConfig(t: TFunction<'offers'>): Record<UnitDecisionDisplayState, BadgeConfig> {
  return {
    ACCEPTED: {
      label: t('workspace.versions.unitDecisions.statusAccepted'),
      icon: Check,
      variant: 'success',
    },
    REJECTED: {
      label: t('workspace.versions.unitDecisions.statusRejected'),
      icon: X,
      variant: 'destructive',
    },
    UNDECIDED: {
      label: t('workspace.versions.unitDecisions.statusUndecided'),
      icon: Clock,
      variant: 'outline',
      tooltip: t('workspace.versions.unitDecisions.statusUndecidedTooltip'),
    },
    SILENT: {
      label: t('workspace.versions.unitDecisions.statusSilent'),
      icon: AlertTriangle,
      variant: 'warning',
      tooltip: t('workspace.versions.unitDecisions.statusSilentTooltip'),
    },
  };
}

// ─── Reservation chip config ────────────────────────────────────

function buildReservationConfig(
  t: TFunction<'offers'>,
): Record<UnitDecisionReservationState, { label: string; dotClassName: string }> {
  return {
    NONE: {
      label: t('workspace.versions.unitDecisions.table.reservationNone'),
      dotClassName: 'bg-status-unavailable',
    },
    SOFT_RESERVATION: {
      label: t('workspace.versions.unitDecisions.table.reservationSoft'),
      dotClassName: 'bg-status-reserved',
    },
    OPTION: {
      label: t('workspace.versions.unitDecisions.table.reservationOption'),
      dotClassName: 'bg-status-option',
    },
    SOLD: {
      label: t('workspace.versions.unitDecisions.table.reservationSold'),
      dotClassName: 'bg-status-sold',
    },
  };
}

// ─── Row tint helper ─────────────────────────────────────────────

/** SILENT rows get an amber tint. */
export function getDecisionRowClassName(unit: UnitDecisionsListItem): string {
  if (getUnitDisplayState(unit) === 'SILENT') return 'bg-amber-50/60';
  return '';
}

// ─── Column factory ───────────────────────────────────────────

export interface BuildUnitDecisionColumnsOptions {
  t: TFunction<'offers'>;
  canViewSupplierDetails?: boolean;
}

export function buildUnitDecisionColumns({
  t,
  canViewSupplierDetails = true,
}: BuildUnitDecisionColumnsOptions): ColumnDef<UnitDecisionsListItem>[] {
  const badgeConfig = buildBadgeConfig(t);
  const reservationConfig = buildReservationConfig(t);

  const columns: ColumnDef<UnitDecisionsListItem>[] = [];

  columns.push(
    {
      id: 'decision',
      label: t('workspace.versions.unitDecisions.table.colDecision'),
      align: 'left',
      width: 200,
      cellClassName: 'px-2 py-3',
      sortable: true,
      sortKey: 'decision',
      renderCell: (unit) => {
        const state = getUnitDisplayState(unit);
        const config = badgeConfig[state];
        const Icon = config.icon;
        const badge = (
          <Badge
            variant={config.variant}
            className="gap-1 whitespace-nowrap px-2 text-xs"
            data-testid={`unit-decision-status-${unit.mediaUnitId}`}
          >
            <Icon className="h-3 w-3 shrink-0" strokeWidth={2} />
            {config.label}
          </Badge>
        );
        if (!config.tooltip) return badge;
        return (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">{badge}</span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-2xs">
                {config.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    ...buildDecisionSharedColumns(t, canViewSupplierDetails),
    {
      id: 'acceptedDates',
      label: t('workspace.versions.unitDecisions.table.colAcceptedDates'),
      align: 'left',
      width: 220,
      renderCell: (unit) => {
        const state = getUnitDisplayState(unit);
        if (state === 'SILENT') {
          return (
            <span className="block truncate text-xs font-medium text-amber-700">
              {t('workspace.versions.unitDecisions.table.noAcceptedDates')}
            </span>
          );
        }
        const formatted = formatAcceptedDates(unit.periods);
        if (!formatted) {
          return <span className="text-xs text-muted-foreground/50">—</span>;
        }
        return (
          <span className="block truncate tabular-nums text-xs text-foreground">{formatted}</span>
        );
      },
    },
    {
      id: 'price',
      label: t('workspace.versions.unitDecisions.table.colPrice'),
      align: 'right',
      width: 100,
      sortable: true,
      sortKey: 'price',
      renderCell: (unit) => (
        <span className="tabular-nums text-xs font-medium">
          {formatOfferedPrice(unit.offeredPricePln)}
        </span>
      ),
    },
    {
      id: 'reservation',
      label: t('workspace.versions.unitDecisions.table.colReservation'),
      align: 'left',
      width: 130,
      resizable: false,
      renderCell: (unit) => {
        const config = reservationConfig[unit.reservationState];
        return (
          <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground"
            data-testid={`unit-decision-reservation-${unit.mediaUnitId}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${config.dotClassName}`} />
            {config.label}
          </span>
        );
      },
    },
    // The mockup's "terms changed since accept" indicator column
    // (table-columns.mockup.tsx's `termsChanged` column) is intentionally
    // omitted here — this internal read endpoint has no cheap cross-version
    // terms-comparison data available yet. Tracked separately; not
    // implemented by this change.
  );

  return columns;
}
